/**
 * File: web/src/components/Header.tsx
 * ==================================
 *
 * Site header (branding + navigation + auth controls)
 *
 * Purpose:
 * - Provide consistent top-of-page branding and primary navigation controls.
 * - Show a single “Sign in / Sign out” button that drives the Cognito Hosted UI flow.
 * - Expose Admin navigation only when the user appears to be an Admin (group claim present).
 *
 * When used:
 * - Rendered by App.tsx on every route (sticky top-level layout).
 * - Receives:
 *   - config: runtime AppConfig (or null while config is loading)
 *   - ready: boolean that gates auth actions until config exists
 *
 * Data flow:
 * - Reads token from sessionStorage (getToken()) to determine “signed in” state.
 * - Determines Admin status from token claims (isAdminToken()).
 * - On click:
 *   - if signed in: logout() clears local token and redirects to Cognito logout
 *   - if signed out: login() redirects to Cognito authorize, storing the current path
 *
 * Learner notes:
 * - Admin gating here is a UX convenience. The backend must still enforce admin authorization.
 * - login(config, location.pathname) preserves where the user was, so after callback they return.
 *
 * Gotchas:
 * - Because token is stored in sessionStorage, a full page refresh clears auth only if session ends;
 *   within the same tab session it persists.
 * - Header reads token synchronously; if you want reactive UI updates, ensure token setters dispatch
 *   an event (your auth.ts does this with "auth-changed") and components/hooks respond accordingly.
 */
import { Link, useLocation } from "react-router-dom";
import type { AppConfig } from "../lib/types";
import { getToken, isAdmin as isAdminToken, login, logout } from "../lib/auth";

export function Header(props: { config: AppConfig | null; ready: boolean }) {
  /**
   * useLocation gives us the current route path.
   * We use it so login() can store a "return here after login" destination.
   */
  const location = useLocation();

  /**
   * token determines signed-in UI state.
   * This is a client-side view of auth state; backend authorizer is the real enforcement.
   */
  const token = getToken();

  /**
   * Admin status is derived from token claims (cognito:groups).
   * This is used only to decide whether to show the Admin link in the header.
   */
  const isAdmin = isAdminToken(token);

  /**
   * Auth button click handler:
   *
   * Behavior:
   * - If config isn't loaded yet, do nothing (button should be disabled via props.ready anyway).
   * - If token exists, we log out (clears local token + redirects to Cognito logout endpoint).
   * - If no token, we log in (redirect to Cognito authorize endpoint with PKCE).
   *
   * Why preserve location.pathname:
   * - After login completes (/callback), we can send the user back to where they started.
   * - This keeps flows smooth (e.g., user clicks Admin → prompted to login → returns to /admin).
   */
  const onAuthClick = async () => {
    if (!props.config) return;
    if (token) {
      // Signed in -> sign out (clear local state + end Hosted UI session).
      logout(props.config);
    } else {
      // Signed out -> start login and return to current route after callback.
      await login(props.config, location.pathname);
    }
  };

  return (
    <header className="site-header" role="banner">
      <div className="container header-inner">
        {/* Branding block: purely presentational */}
        <div
          className="brand"
          aria-label="Washington State University Global Campus"
        >
          <div className="brand-mark" aria-hidden="true">
            WSU
          </div>
          <div>
            <div className="brand-title">Secure Upload Portal</div>
            <div className="brand-subtitle">
              Washington State University Global Campus
            </div>
          </div>
        </div>
        {/* Action area: admin link + auth toggle */}
        <div className="header-actions">
          {/**
           * Admin link:
           * - shown only if:
           *   - user has a token AND
           *   - token claims indicate membership in the Admins group
           *
           * Learner note:
           * - This is not security. It just hides the link for non-admins.
           * - The Admin API endpoints must still enforce group membership server-side.
           */}
          {token && isAdmin ? (
            <Link className="header-link" to="/admin">
              Admin
            </Link>
          ) : null}
          {/**
           * Auth button is disabled until runtime config is loaded:
           * - login/logout require Cognito domain + clientId from config.json
           * - preventing clicks early avoids confusing errors
           */}
          <button
            className="btn btn-invert"
            type="button"
            onClick={onAuthClick}
            disabled={!props.ready}
          >
            {token ? "Sign out" : "Sign in"}
          </button>
        </div>
      </div>
    </header>
  );
}
